import { Button, Modal, SelectOptionType, addToast } from '@octano/global-ui';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import { addTutor } from '../../api';
import { useTexts } from '../../hooks/useTexts';
import { AssignedStudentModalForm } from './AssignedStudentModalForm';

interface Props {
  onConfirm: () => void;
  onDecline?: () => void;
}

export interface AssignedStudentModalRef {
  closeModal: () => void;
  openModal: () => void;
}

interface ModalFormData {
  studentId: SelectOptionType | null;
  studyPlanVersion: SelectOptionType | null;
  school: string | null;
  campus: string | null;
  schedule: string | null;
}

const AssignedStudentModal = forwardRef<AssignedStudentModalRef, Props>(
  ({ onConfirm, onDecline }, ref) => {
    const { teacherId } = useParams<{ teacherId: string }>();
    const texts = useTexts();

    const forms = useForm({
      mode: 'onChange',
    });

    const {
      reset,
      handleSubmit,
      formState: { isSubmitting },
    } = forms;

    const [modal, setModal] = useState(false);

    const openModal = useCallback(() => {
      reset({});
      setModal(true);
    }, [reset]);

    const closeModal = useCallback(() => {
      reset({});
      setModal(false);
    }, [reset]);

    useImperativeHandle(ref, () => ({
      openModal: openModal,
      closeModal: closeModal,
    }));

    const onSubmit = async (values: ModalFormData) => {
      const dataForm = {
        studentId: values?.studentId?.value ?? null,
        studyPlanEnrollmentId: values?.studyPlanVersion?.enrollmentId ?? null,
      };

      if (dataForm.studyPlanEnrollmentId && dataForm.studentId && teacherId) {
        const { data, error } = await addTutor(teacherId, dataForm);
        if (data && error === false) {
          addToast({
            icon: 'success',
            color: 'success',
            text: texts.modal.saveSuccess,
          });
          onConfirm();
        } else if (error) {
          addToast({
            color: 'danger',
            icon: 'error',
            text: texts.modal.saveError,
          });
        }
      }
      setModal(false);
    };

    return (
      <Modal isOpen={modal} toggle={closeModal} size="lg">
        <Container fluid>
          <h1 className="text-dark fs-20 mt-3 mb-4 text-center">
            {texts.modal.title}
          </h1>
          <p className="fs-16 mb-5 text-center">{texts.modal.subtitle}</p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormProvider {...forms}>
              <AssignedStudentModalForm />
              <Row xs={12}>
                <Col md={12} xs={12} className="pb-5" />
                <Col xs={12} md={6}>
                  <Button
                    outlined
                    text={texts.modal.actions.cancel}
                    fullwidth
                    onClick={() => {
                      reset();
                      onDecline && onDecline();
                      setModal(false);
                    }}
                    disabled={isSubmitting}
                    className="mb-3"
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Button
                    type="submit"
                    text={texts.modal.actions.assign}
                    className="mb-3"
                    fullwidth
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </Col>
              </Row>
            </FormProvider>
          </form>
        </Container>
      </Modal>
    );
  },
);

export default AssignedStudentModal;
